// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import EditLink from 'Ui/components/common/data-table/edit-link';
import EquipmentName from 'OnEquip/equipment/common/data-table/equipment-name';
import ServiceStatus from 'OnEquip/equipment/common/data-table/service-status';
import {getMaintenanceDue} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {formatDateOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {
    alphaNumericCompare,
    dateCompare,
    formatAssignees,
    formatModelAndManufacturerName, genericServiceCompare,
    numericCompare
} from 'Ui/models/maintenance';
import {useEquipmentContext} from 'OnEquip/equipment/context/equipment-context';
import {MAINTENANCE} from 'OnEquip/equipment/constants/equipment-details-tabs';
import {SHOP} from 'Common/constants/routes';
import {IN_PROCESS, SCHEDULED} from 'Common/constants/service-status';
import accounting from 'accounting';
import {VIEW_WORKBOARDS} from 'Common/constants/business-activities';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations, membershipId, history) {
    const onequipUrl = `${window.props.onLinkLegacyUri}/onequip/#m/${membershipId}/fleet/{0}/shop`;
    const equipmentUrl = `${onequipUrl}/equipment/{1}`;
    const serviceUrl = `${SHOP}/serviceForm?serviceId={0}`;

    return [
        {
            headerName: translations.DATE,
            field: 'formattedDate',
            minWidth: 100,
            flex: 1,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, dateCompare, 'dateScheduled'),
            renderCell: (params) => {
                return params.row.formattedDate;
            }
        },
        {
            headerName: translations.STATUS,
            field: 'status',
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                return (
                    <ServiceStatus
                        status={params.row.status}
                        translations={translations}
                    />
                );
            }
        },
        {
            headerName: translations.ONLINK_ASSIGNEES,
            field: 'assignees',
            flex: 1,
            minWidth: 150,
            sortComparator: alphaNumericCompare,
            requiredMyJdPermissions: VIEW_WORKBOARDS
        },
        {
            headerName: translations.EQUIPMENT_LIST,
            field: 'equipmentName',
            flex: 1,
            minWidth: 150,
            sortComparator: alphaNumericCompare,
            renderCell: (params) => {
                return (
                    <EquipmentName
                        equipment={params.row}
                        tab={MAINTENANCE}
                        type={SHOP}
                        url={equipmentUrl}
                    />
                );
            }
        },
        {
            headerName: translations.MODEL,
            flex: 1,
            minWidth: 120,
            field: 'modelAndManufacturerName',
            sortComparator: alphaNumericCompare
        },
        {
            headerName: translations.TYPE,
            field: 'equipmentTypeName',
            sortComparator: alphaNumericCompare,
            minWidth: 140,
            flex: 1
        },
        {
            headerName: translations.ONLINK_GROUP,
            field: 'serviceGroup',
            sortComparator: alphaNumericCompare,
            minWidth: 130,
            flex: 1
        },
        {
            headerName: translations.ONLINK_SERVICE,
            field: 'serviceTypeName',
            flex: 1,
            sortComparator: alphaNumericCompare,
            renderCell: (params) => {
                const {row: service} = params;

                const filledInServiceUrl = serviceUrl.replace('{0}', service.serviceId);

                return (
                    <EditLink onClick={() => history.push(filledInServiceUrl)}>
                        {service.serviceTypeName}
                    </EditLink>
                );
            }
        },
        {
            headerName: replaceTranslationNames(translations.ONLINK_EST_WITH_UNIT, {
                '0': translations.mins
            }),
            field: 'estDuration',
            type: 'number',
            sortComparator: numericCompare,
            width: 120,
            renderCell: (params) => {
                return accounting.formatNumber(params.row.estDuration, 0);
            }
        },
        {
            headerName: replaceTranslationNames(translations.ONLINK_ACT_WITH_UNIT, {
                '0': translations.mins
            }),
            field: 'actualDuration',
            type: 'number',
            sortComparator: numericCompare,
            width: 150,
            renderCell: (params) => {
                return accounting.formatNumber(params.row.actualDuration, 0);
            }
        }
    ];
}

function Shop(props) {
    const {
        history,
        membershipId,
        translations
    } = props;

    const [services, setServices] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const equipmentContext = useEquipmentContext();

    equipmentContext.current = async (isMounted = () => true) => {
        setLoading(true);

        const services = await getMaintenanceDue(`${IN_PROCESS},${SCHEDULED}`);

        if (isMounted()) {
            const formattedServices = services.map((service) => ({
                ...service,
                assignees: formatAssignees(service),
                formattedDate: formatDateOrDefault(service.dateScheduled),
                modelAndManufacturerName: formatModelAndManufacturerName(service)
            }));

            setServices(formattedServices);
            setLoading(false);
        }
    };

    React.useEffect(() => fetchEffectData(equipmentContext.current), [membershipId]);

    return (
        <DataGridWrapper
            columns={getDataGridColumns(translations, membershipId, history)}
            getRowId={(row) => row.serviceId}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'formattedDate',
                        sort: 'asc'
                    }]
                }
            }}
            loading={loading}
            noDataAvailableMessage={translations.ONLINK_NO_SCHEDULED_OR_IN_PROCESS_SERVICE}
            rows={services}
            searchFields={['formattedName', 'status', 'assignees', 'equipmentName', 'modelAndManufacturerName', 'equipmentTypeName', 'serviceGroup', 'serviceTypeName', 'estDuration', 'actualDuration']}
            searchable={true}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );
}

Shop.propTypes = {
    history: PropTypes.history,
    location: PropTypes.location,
    membershipId: PropTypes.string,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membershipId: state.membership.membershipId,
        translations: state.translations
    };
}

export default connect(mapStateToProps)(withRouter(Shop));
